import { Check, MoveRight, Star, Loader2 } from "lucide-react";
import { Badge } from "./badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./card";
import { Button } from "./button";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { createSubscription } from "../../services/stripeService";
import { useState } from "react";
import toast from "react-hot-toast";
import { useThemeMode } from "../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";

function HealthTreePricing() {
  const { user, loading: authLoading } = useAuth();
  const { isPremium, isLoading: subscriptionLoading } = useSubscription();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const isDark = mode === 'dark';

  // Show loading skeleton if auth is still loading
  if (authLoading) {
    return (
      <div className="w-full py-8 lg:py-12">
        <div className="container mx-auto px-4">
          <div className="flex text-center justify-center items-center gap-4 flex-col">
            <div className="flex gap-2 flex-col">
              <h2 className={`text-3xl md:text-5xl tracking-tighter max-w-xl text-center font-regular ${isDark ? 'text-white' : 'text-black'}`}>
                Unlock Your Full Potential
              </h2>
              <p className={`text-lg max-w-xl text-center ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                Choose a plan that works for you and start your health transformation journey today.
              </p>
            </div>
            <div className="grid text-left grid-cols-1 lg:grid-cols-3 gap-6 w-full max-w-6xl mt-8">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-full p-6 rounded-3xl border ${
                    isDark ? 'bg-white/5 border-white/10' : 'bg-white/70 border-white/30'
                  } animate-pulse`}
                >
                  <div className="space-y-4">
                    <div className={`h-6 rounded ${isDark ? 'bg-white/20' : 'bg-black/20'}`}></div>
                    <div className={`h-12 rounded ${isDark ? 'bg-white/20' : 'bg-black/20'}`}></div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((j) => (
                        <div key={j} className={`h-4 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`}></div>
                      ))}
                    </div>
                    <div className={`h-12 rounded ${isDark ? 'bg-white/20' : 'bg-black/20'} flex items-center justify-center`}>
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleUpgrade = async (planType: 'monthly' | 'annual') => {

    try {
      setLoading(true);
      
      // Get the price ID based on plan type
      const priceId = planType === 'monthly' 
        ? 'price_1NjT8IBH0i49Oiov7d6px8Xu'  // Monthly price ID
        : 'price_1NjTLMBH0i49OiovHoU0R9OU'; // Annual price ID
      
      await createSubscription({ 
        userId: user!.id, 
        userEmail: user!.email,
        userName: user!.name,
        priceId 
      });
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="w-full py-8 lg:py-12"
      style={{
        background: 'transparent',
        position: 'relative',
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex text-center justify-center items-center gap-4 flex-col">
          <div className="flex gap-2 flex-col">
            <h2 className={`text-3xl md:text-5xl tracking-tighter max-w-xl text-center font-regular ${isDark ? 'text-white' : 'text-black'}`}>
              Unlock Your Full Potential
            </h2>
            <p className={`text-lg leading-relaxed tracking-tight max-w-xl text-center ${isDark ? 'text-white/70' : 'text-black/70'}`}>
              Transform Your Personal Health journey with Evidence-based protocols
            </p>
          </div>
          <div className="grid pt-12 text-left grid-cols-1 lg:grid-cols-3 w-full gap-6 justify-center max-w-6xl mx-auto">
            <Card 
              className="w-full flex flex-col"
              style={{
                background: isDark 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '24px',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: isDark
                  ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                  : '0 8px 32px rgba(31, 38, 135, 0.15)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              <CardHeader>
                <CardTitle>
                  <span className={`flex flex-row gap-4 items-center font-normal ${isDark ? 'text-white' : 'text-black'}`}>
                    Free Plan
                  </span>
                </CardTitle>
                <CardDescription className={isDark ? 'text-white/70' : 'text-black/70'}>
                  Get started with basic goal tracking and talent tree features.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 flex-1 flex flex-col">
                <div className="flex flex-col gap-6 justify-start flex-1">
                  <p className={`flex flex-row items-center gap-2 text-xl ${isDark ? 'text-white' : 'text-black'}`}>
                    <span className="text-4xl">$0</span>
                    <span className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                      / month
                    </span>
                  </p>
                  <div className="flex flex-col gap-4 justify-start">
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Basic Goal Tracking</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Track up to 3 active goals
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>View-only access to Talent trees</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Browse and explore talent tree structure
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Health Worth Tracking</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Track your health worth
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto">
                    <Button 
                      variant="outline" 
                      className="gap-4 w-full"
                      disabled={!!user && !isPremium}
                      onClick={() => {
                        if (!user) {
                          navigate('/register');
                        }
                      }}
                    >
                      {user && !isPremium ? "Current Plan" : "Get Started"} 
                      <MoveRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="w-full border-green-500 relative flex flex-col"
              style={{
                background: isDark 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '24px',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: isDark
                  ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                  : '0 8px 32px rgba(31, 38, 135, 0.15)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground">
                  <Star className="w-3 h-3 mr-1" />
                  Most Popular
                </Badge>
              </div>
              <CardHeader>
                <CardTitle>
                  <span className={`flex flex-row gap-4 items-center font-normal ${isDark ? 'text-white' : 'text-black'}`}>
                    Premium Annual
                  </span>
                </CardTitle>
                <CardDescription className={isDark ? 'text-white/70' : 'text-black/70'}>
                  Get the best value with our annual plan and save significantly.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 flex-1 flex flex-col">
                <div className="flex flex-col gap-6 justify-start flex-1">
                  <div className="flex flex-col gap-2">
                    <p className={`flex flex-row items-center gap-2 text-xl ${isDark ? 'text-white' : 'text-black'}`}>
                      <span className="text-4xl">$185</span>
                      <span className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                        / year
                      </span>
                    </p>
                    <p className="text-sm text-green-600 font-medium">
                      Save $42 compared to monthly
                    </p>
                    <p className={`text-xs ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                      Equivalent to $15.42/month
                    </p>
                  </div>
                  <div className="flex flex-col gap-4 justify-start">
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Everything in Premium Monthly</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          All premium features included
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Save $42 per year</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Pay annually and save money
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Exclusive Annual Perks</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Special features for annual subscribers
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Priority Feature Requests</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Help shape the future of Health-Tree
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Extended Support</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Premium support with faster response times
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto">
                    {isPremium ? (
                      <Button className="gap-4 w-full" disabled>
                        Current Plan <Check className="w-4 h-4" />
                      </Button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Button 
                          className="gap-4 bg-green-600 hover:bg-green-700 w-full" 
                          onClick={() => {
                            if (!user) {
                              navigate('/login');
                            } else {
                              handleUpgrade('annual');
                            }
                          }}
                          disabled={loading || subscriptionLoading || authLoading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Processing...
                            </>
                          ) : authLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading...
                            </>
                          ) : user ? (
                            <>
                              Start Annual Plan
                              <MoveRight className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Please Sign In to Upgrade
                              <MoveRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className="w-full border-primary relative flex flex-col"
              style={{
                background: isDark 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '24px',
                border: isDark
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: isDark
                  ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                  : '0 8px 32px rgba(31, 38, 135, 0.15)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              <CardHeader>
                <CardTitle>
                  <span className={`flex flex-row gap-4 items-center font-normal ${isDark ? 'text-white' : 'text-black'}`}>
                    Premium Monthly
                  </span>
                </CardTitle>
                <CardDescription className={isDark ? 'text-white/70' : 'text-black/70'}>
                  Unlock unlimited potential with advanced features and priority support.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 flex-1 flex flex-col">
                <div className="flex flex-col gap-6 justify-start flex-1">
                  <p className={`flex flex-row items-center gap-2 text-xl ${isDark ? 'text-white' : 'text-black'}`}>
                    <span className="text-4xl">$18.99</span>
                    <span className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                      / month
                    </span>
                  </p>
                  <div className="flex flex-col gap-4 justify-start">
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Unlimited Goals & Tracking</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Track unlimited goals with advanced analytics
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Advanced Talent Trees</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Access to all talent trees and weekly lock-ins
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Detailed Analytics</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Comprehensive insights and progress reports
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Priority Support</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Get help when you need it most
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <Check className={`w-4 h-4 mt-2 ${isDark ? 'text-white' : 'text-black'}`} />
                      <div className="flex flex-col">
                        <p className={isDark ? 'text-white' : 'text-black'}>Health Worth Tracking</p>
                        <p className={`text-sm ${isDark ? 'text-white/70' : 'text-black/70'}`}>
                          Track your health worth
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto">
                    {isPremium ? (
                      <Button className="gap-4 w-full" disabled>
                        Current Plan <Check className="w-4 h-4" />
                      </Button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <Button 
                          className="gap-4 w-full" 
                          onClick={() => {
                            if (!user) {
                              navigate('/login');
                            } else {
                              handleUpgrade('monthly');
                            }
                          }}
                          disabled={loading || subscriptionLoading || authLoading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Processing...
                            </>
                          ) : authLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading...
                            </>
                          ) : user ? (
                            <>
                              Start Monthly Plan
                              <MoveRight className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Please Sign In to Upgrade
                              <MoveRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export { HealthTreePricing };
